import { VariableKind } from '../constants'
import { useState, useCallback, useMemo, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import type { TemplateVariable, PayloadFormat } from '../types'
import {
  buildJsonSkeleton,
  buildXmlSkeleton,
  extractValuesFromJson,
  setValueInJson,
  buildPayloadFromForm,
  extractLoopRowsFromJson,
  emptyLoopRow,
  type LoopRow,
} from '../utils/payloadSkeleton'
import { buildSampleFormState } from '../utils/sampleData'
import { colors, spacing, radius, fontSize } from '../styles/theme'
import Toggle from './Toggle'


export interface PayloadEditorHandle {
  loadPayload: (json: string) => void
  getPayload: () => string
  insertVariable: (rawName: string) => void
  hasErrors: () => boolean
}

interface PayloadEditorProps {
  variables: TemplateVariable[]
  onChange: (payload: string, format: PayloadFormat) => void
  onRender: (payload: string, format: PayloadFormat) => void
  renderLoading?: boolean
}

type EditorMode = 'form' | 'raw'

const PayloadEditor = forwardRef<PayloadEditorHandle, PayloadEditorProps>(
function PayloadEditor({ variables, onChange, onRender, renderLoading }, ref) {
  const [format, setFormat] = useState<PayloadFormat>('json')
  const [mode, setMode] = useState<EditorMode>('form')
  const [rawPayload, setRawPayload] = useState<string>(() => buildJsonSkeleton(variables))
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const rawPayloadRef = useRef(rawPayload)
  const validationIssuesRef = useRef<{ kind: 'error' | 'warning'; message: string }[]>([])
  useEffect(() => { rawPayloadRef.current = rawPayload }, [rawPayload])

  // Scalar form values (top-level, non-loop-scoped)
  const [scalarValues, setScalarValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      variables
        .filter(v => v.variableKindLookupId === VariableKind.SCALAR && !v.loopKey)
        .map(v => [v.normalizedKey, ''])
    )
  )

  // Conditional checkboxes
  const [conditionalValues, setConditionalValues] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      variables
        .filter(v => v.variableKindLookupId === VariableKind.CONDITIONAL)
        .map(v => [v.normalizedKey, false])
    )
  )

  // Loop rows: loopKey → array of row objects
  const [loopRows, setLoopRows] = useState<Record<string, LoopRow[]>>(() => {
    const init: Record<string, LoopRow[]> = {}
    for (const v of variables) {
      if (v.variableKindLookupId !== VariableKind.LOOP) continue
      const subFields = variables.filter(sf => sf.loopKey === v.normalizedKey)
      init[v.normalizedKey] = [emptyLoopRow(subFields)]
    }
    return init
  })

  const buildAndNotify = useCallback((
    sv: Record<string, string>,
    cv: Record<string, boolean>,
    lr: Record<string, LoopRow[]>,
    fmt: PayloadFormat,
  ) => {
    if (fmt === 'json') {
      const payload = buildPayloadFromForm(variables, sv, cv, lr)
      setRawPayload(payload)
      onChange(payload, fmt)
    }
  }, [variables, onChange])

  const handleFormatChange = useCallback((newFormat: PayloadFormat) => {
    setFormat(newFormat)
    const skeleton = newFormat === 'xml' ? buildXmlSkeleton(variables) : buildJsonSkeleton(variables)
    setRawPayload(skeleton)
    setScalarValues(Object.fromEntries(
      variables
        .filter(v => v.variableKindLookupId === VariableKind.SCALAR && !v.loopKey)
        .map(v => [v.normalizedKey, ''])
    ))
    setConditionalValues(Object.fromEntries(
      variables.filter(v => v.variableKindLookupId === VariableKind.CONDITIONAL).map(v => [v.normalizedKey, false])
    ))
    const newLoopRows: Record<string, LoopRow[]> = {}
    for (const v of variables) {
      if (v.variableKindLookupId !== VariableKind.LOOP) continue
      const subFields = variables.filter(sf => sf.loopKey === v.normalizedKey)
      newLoopRows[v.normalizedKey] = [emptyLoopRow(subFields)]
    }
    setLoopRows(newLoopRows)
    onChange(skeleton, newFormat)
  }, [variables, onChange])

  /**
   * Fills every variable with a plausible example value, re-rolled on each press.
   *
   * Both halves of the editor are written, not just the one on screen: the form state so Form mode
   * shows the values in their fields, and the raw payload so Raw mode and the render that follows
   * see the same thing. Writing only the visible one would make switching modes wipe the sample.
   *
   * XML is built from the same values through the normal form-to-payload path.
   */
  const handleFillSample = useCallback(() => {
    const { scalarValues: sv, conditionalValues: cv, loopRows: lr } = buildSampleFormState(variables)
    setScalarValues(sv)
    setConditionalValues(cv)
    setLoopRows(lr)
    const payload = buildPayloadFromForm(variables, sv, cv, lr)
    setRawPayload(payload)
    onChange(payload, format)
  }, [variables, format, onChange])

  const handleRawChange = useCallback((raw: string) => {
    setRawPayload(raw)
    if (format === 'json') {
      const flat = extractValuesFromJson(raw)
      setScalarValues(prev => {
        const next = { ...prev }
        for (const k of Object.keys(next)) next[k] = flat[k] ?? ''
        return next
      })
      setLoopRows(extractLoopRowsFromJson(raw, variables))
    }
    onChange(raw, format)
  }, [format, variables, onChange])

  const insertVariable = useCallback((rawName: string) => {
    const tag = `{{${rawName}}}`
    const ta = textareaRef.current
    if (!ta) { handleRawChange(rawPayloadRef.current + tag); return }
    const start = ta.selectionStart
    const end   = ta.selectionEnd
    const next  = rawPayloadRef.current.slice(0, start) + tag + rawPayloadRef.current.slice(end)
    handleRawChange(next)
    requestAnimationFrame(() => {
      ta.focus()
      ta.setSelectionRange(start + tag.length, start + tag.length)
    })
  }, [handleRawChange])

  useImperativeHandle(ref, () => ({
    loadPayload(json: string) { handleRawChange(json); setMode('raw') },
    getPayload() { return rawPayloadRef.current },
    insertVariable(rawName: string) { insertVariable(rawName) },
    hasErrors() { return validationIssuesRef.current.some(i => i.kind === 'error') },
  }), [handleRawChange, insertVariable])

  const handleScalarChange = useCallback((key: string, value: string) => {
    const next = { ...scalarValues, [key]: value }
    setScalarValues(next)
    if (format === 'json') {
      const payload = setValueInJson(rawPayload, key, value)
      setRawPayload(payload)
      onChange(payload, format)
    }
  }, [scalarValues, rawPayload, format, onChange])

  const handleConditionalChange = useCallback((key: string, checked: boolean) => {
    const next = { ...conditionalValues, [key]: checked }
    setConditionalValues(next)
    buildAndNotify(scalarValues, next, loopRows, format)
  }, [conditionalValues, scalarValues, loopRows, format, buildAndNotify])

  const handleLoopFieldChange = useCallback((loopKey: string, rowIdx: number, fieldKey: string, value: string) => {
    const rows = loopRows[loopKey] ?? []
    const newRows = rows.map((row, i) => i === rowIdx ? { ...row, [fieldKey]: value } : row)
    const next = { ...loopRows, [loopKey]: newRows }
    setLoopRows(next)
    buildAndNotify(scalarValues, conditionalValues, next, format)
  }, [loopRows, scalarValues, conditionalValues, format, buildAndNotify])

  const addLoopRow = useCallback((loopKey: string) => {
    const subFields = variables.filter(sf => sf.loopKey === loopKey)
    const newRows = [...(loopRows[loopKey] ?? []), emptyLoopRow(subFields)]
    const next = { ...loopRows, [loopKey]: newRows }
    setLoopRows(next)
    buildAndNotify(scalarValues, conditionalValues, next, format)
  }, [loopRows, variables, scalarValues, conditionalValues, format, buildAndNotify])

  const removeLoopRow = useCallback((loopKey: string, rowIdx: number) => {
    const rows = loopRows[loopKey] ?? []
    if (rows.length <= 1) return
    const newRows = rows.filter((_, i) => i !== rowIdx)
    const next = { ...loopRows, [loopKey]: newRows }
    setLoopRows(next)
    buildAndNotify(scalarValues, conditionalValues, next, format)
  }, [loopRows, scalarValues, conditionalValues, format, buildAndNotify])

  const topScalars = variables.filter(v => v.variableKindLookupId === VariableKind.SCALAR && !v.loopKey)
  const conditionals = variables.filter(v => v.variableKindLookupId === VariableKind.CONDITIONAL)
  const loops = variables.filter(v => v.variableKindLookupId === VariableKind.LOOP)

  const validationIssues = useMemo<{ kind: 'error' | 'warning'; message: string }[]>(() => {
    const issues: { kind: 'error' | 'warning'; message: string }[] = []

    if (mode === 'raw' && format === 'json') {
      try {
        JSON.parse(rawPayload)
      } catch (e: unknown) {
        issues.push({ kind: 'error', message: `Invalid JSON: ${(e as Error).message}` })
        validationIssuesRef.current = issues
        return issues
      }
    }

    if (mode === 'form') {
      const missing = topScalars.filter(v => !(scalarValues[v.normalizedKey] ?? '').trim())
      if (missing.length > 0)
        issues.push({ kind: 'warning', message: `Empty fields: ${missing.map(v => v.rawName).join(', ')}` })
    } else if (format === 'json') {
      try {
        const parsed = JSON.parse(rawPayload)
        const missing = topScalars.filter(v => !resolveNestedPath(parsed, v.normalizedKey))
        if (missing.length > 0)
          issues.push({ kind: 'warning', message: `Missing variables: ${missing.map(v => v.rawName).join(', ')}` })
        const knownRoots = new Set(variables.map(v => v.normalizedKey.split('.')[0].replace(/_/g, '').toLowerCase()))
        const extra = Object.keys(parsed).filter(k => !knownRoots.has(k.replace(/_/g, '').toLowerCase()))
        if (extra.length > 0)
          issues.push({ kind: 'warning', message: `Unknown keys (not in template): ${extra.join(', ')}` })
      } catch { /* parse error handled above */ }
    }
    validationIssuesRef.current = issues
    return issues
  }, [mode, format, rawPayload, topScalars, scalarValues, variables])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' }}>
        <span style={{ fontSize: fontSize.sm, color: colors.textSecondary, fontWeight: 600 }}>
          Payload Format
        </span>
        <ToggleGroup
          options={[{ value: 'json', label: 'JSON' }, { value: 'xml', label: 'XML' }]}
          value={format}
          onChange={v => handleFormatChange(v as PayloadFormat)}
        />
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={handleFillSample}
          disabled={variables.length === 0}
          title={variables.length === 0
            ? 'This template has no variables to fill'
            : 'Fill every variable with example data. Press again for a different set.'}
          style={{
            padding: `${spacing.xs} ${spacing.sm}`,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.sm,
            background: colors.surface,
            color: variables.length === 0 ? colors.textSecondary : colors.text,
            fontSize: fontSize.sm,
            cursor: variables.length === 0 ? 'not-allowed' : 'pointer',
            opacity: variables.length === 0 ? 0.6 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          Fill with sample data
        </button>
        <ToggleGroup
          options={[{ value: 'form', label: 'Form' }, { value: 'raw', label: 'Raw' }]}
          value={mode}
          onChange={v => setMode(v as EditorMode)}
        />
      </div>

      {/* Form mode */}
      {mode === 'form' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          {format === 'xml' && (
            <div style={{
              padding: spacing.sm,
              background: colors.warningLight,
              borderRadius: radius.sm,
              fontSize: fontSize.sm,
              color: colors.warning,
            }}>
              Form mode shows scalar variables only. Switch to Raw for full XML control.
            </div>
          )}

          {/* Top-level scalars */}
          {topScalars.length === 0 && loops.length === 0 && conditionals.length === 0 && (
            <div style={{ fontSize: fontSize.sm, color: colors.textMuted }}>
              No variables found in this template.
            </div>
          )}
          {topScalars.map(v => (
            <ScalarField
              key={v.normalizedKey}
              variable={v}
              value={scalarValues[v.normalizedKey] ?? ''}
              onChange={val => handleScalarChange(v.normalizedKey, val)}
              hasValue={!!(scalarValues[v.normalizedKey] ?? '').trim()}
            />
          ))}

          {/* Conditionals */}
          {conditionals.map(v => (
            <div key={v.normalizedKey} style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
              <Toggle
                id={`cond-${v.normalizedKey}`}
                checked={conditionalValues[v.normalizedKey] ?? false}
                onChange={checked => handleConditionalChange(v.normalizedKey, checked)}
              />
              <label htmlFor={`cond-${v.normalizedKey}`} style={{
                fontSize: fontSize.sm, fontWeight: 500, color: colors.text, cursor: 'pointer'
              }}>
                {v.rawName}
                {v.contextSnippet && (
                  <span style={{ color: colors.textMuted, fontWeight: 400, marginLeft: '6px' }}>
                    — {v.contextSnippet}
                  </span>
                )}
              </label>
            </div>
          ))}

          {/* Loop sections */}
          {loops.map(lv => {
            const subFields = variables.filter(sf => sf.loopKey === lv.normalizedKey)
            const rows = loopRows[lv.normalizedKey] ?? []
            return (
              <div key={lv.normalizedKey} style={{
                border: `1px solid ${colors.border}`,
                borderRadius: radius.md,
                overflow: 'hidden',
              }}>
                {/* Section header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: `${spacing.sm} ${spacing.md}`,
                  background: colors.background,
                  borderBottom: `1px solid ${colors.border}`,
                }}>
                  <span style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.text }}>
                    {lv.rawName}
                    <span style={{ color: colors.textMuted, fontWeight: 400, marginLeft: '6px' }}>
                      repeating section ({rows.length} {rows.length === 1 ? 'row' : 'rows'})
                    </span>
                  </span>
                  <button
                    onClick={() => addLoopRow(lv.normalizedKey)}
                    style={{
                      padding: `2px 10px`,
                      background: colors.primary,
                      color: '#fff',
                      border: 'none',
                      borderRadius: radius.sm,
                      fontSize: fontSize.sm,
                      cursor: 'pointer',
                    }}
                  >
                    + Add row
                  </button>
                </div>

                {/* Rows */}
                {rows.map((row, rowIdx) => (
                  <div key={rowIdx} style={{
                    padding: spacing.md,
                    borderBottom: rowIdx < rows.length - 1 ? `1px solid ${colors.border}` : undefined,
                    background: rowIdx % 2 === 1 ? colors.background : colors.surface,
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: spacing.sm,
                    }}>
                      <span style={{ fontSize: fontSize.sm, color: colors.textMuted }}>
                        Row {rowIdx + 1}
                      </span>
                      {rows.length > 1 && (
                        <button
                          onClick={() => removeLoopRow(lv.normalizedKey, rowIdx)}
                          style={{
                            padding: `1px 8px`,
                            background: 'transparent',
                            color: colors.textMuted,
                            border: `1px solid ${colors.border}`,
                            borderRadius: radius.sm,
                            fontSize: fontSize.sm,
                            cursor: 'pointer',
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                      {subFields.map(sf => (
                        <ScalarField
                          key={sf.normalizedKey}
                          variable={sf}
                          value={row[sf.normalizedKey] ?? ''}
                          onChange={val => handleLoopFieldChange(lv.normalizedKey, rowIdx, sf.normalizedKey, val)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      )}

      {/* Raw mode */}
      {mode === 'raw' && variables.length > 0 && (
        <VariableBrowser variables={variables} onInsert={insertVariable} rawPayload={rawPayload} />
      )}
      {mode === 'raw' && format === 'json' && (
        <JsonPathBrowser rawPayload={rawPayload} onInsert={insertVariable} />
      )}
      {mode === 'raw' && (
        <textarea
          ref={textareaRef}
          value={rawPayload}
          onChange={e => handleRawChange(e.target.value)}
          spellCheck={false}
          style={{
            width: '100%',
            minHeight: 240,
            padding: spacing.md,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.sm,
            fontSize: '12px',
            fontFamily: 'ui-monospace, "Cascadia Code", "Source Code Pro", monospace',
            color: colors.text,
            background: colors.surface,
            resize: 'vertical',
            boxSizing: 'border-box',
            outline: 'none',
            lineHeight: 1.5,
          }}
          onFocus={e => { e.target.style.borderColor = colors.borderFocus }}
          onBlur={e => { e.target.style.borderColor = colors.border }}
        />
      )}

      {validationIssues.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {validationIssues.map((issue, i) => (
            <div key={i} style={{
              padding: `${spacing.xs} ${spacing.sm}`,
              borderRadius: radius.sm,
              fontSize: fontSize.sm,
              background: issue.kind === 'error' ? colors.errorLight : colors.warningLight,
              color: issue.kind === 'error' ? colors.error : colors.warning,
              border: `1px solid ${issue.kind === 'error' ? '#fecaca' : '#fde68a'}`,
            }}>
              {issue.message}
            </div>
          ))}
        </div>
      )}

      <button
        onClick={() => onRender(rawPayload, format)}
        disabled={renderLoading || validationIssues.some(i => i.kind === 'error')}
        style={{
          width: '100%',
          padding: `${spacing.sm} ${spacing.md}`,
          background: renderLoading || validationIssues.some(i => i.kind === 'error')
            ? colors.textMuted
            : colors.primary,
          color: '#fff',
          border: 'none',
          borderRadius: radius.sm,
          fontSize: fontSize.base,
          fontWeight: 600,
          cursor: renderLoading || validationIssues.some(i => i.kind === 'error') ? 'default' : 'pointer',
          marginTop: spacing.xs,
        }}
      >
        {renderLoading ? 'Generating…' : 'Generate Preview'}
      </button>
    </div>
  )
})

export default PayloadEditor

const normalize = (k: string) => k.replace(/_/g, '').toLowerCase()

function JsonPathBrowser({ rawPayload, onInsert }: { rawPayload: string; onInsert: (path: string) => void }) {
  const [open, setOpen] = useState(false)

  const paths = useMemo(() => {
    try {
      return extractLeafPaths(JSON.parse(rawPayload), '')
    } catch { return [] }
  }, [rawPayload])

  if (paths.length === 0) return null

  return (
    <div style={{ border: `1px solid ${colors.border}`, borderRadius: radius.sm, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%',
          padding: `${spacing.xs} ${spacing.sm}`,
          background: colors.background,
          border: 'none',
          textAlign: 'left',
          fontSize: fontSize.sm,
          fontWeight: 500,
          color: colors.textSecondary,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: spacing.xs,
        }}
      >
        <span style={{ fontSize: '10px' }}>{open ? '▾' : '▸'}</span>
        Data paths
        <span style={{ marginLeft: 'auto', fontSize: '11px', color: colors.textMuted }}>{paths.length}</span>
      </button>
      {open && (
        <div style={{ padding: spacing.sm, display: 'flex', flexWrap: 'wrap', gap: '4px', background: colors.surface }}>
          {paths.map(path => (
            <button
              key={path}
              onClick={() => onInsert(path)}
              title={`Insert {{${path}}}`}
              style={{
                padding: '2px 8px',
                border: `1px solid ${colors.border}`,
                borderRadius: 999,
                background: colors.background,
                color: colors.textSecondary,
                fontSize: '11px',
                fontFamily: 'monospace',
                cursor: 'pointer',
              }}
            >
              {path}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function extractLeafPaths(obj: unknown, prefix: string): string[] {
  if (obj === null || typeof obj !== 'object') return prefix ? [prefix] : []
  if (Array.isArray(obj)) {
    const first = obj[0]
    if (first !== null && typeof first === 'object' && !Array.isArray(first))
      return extractLeafPaths(first, prefix)
    return prefix ? [prefix] : []
  }
  const paths: string[] = []
  for (const [key, val] of Object.entries(obj as Record<string, unknown>)) {
    const path = prefix ? `${prefix}.${key}` : key
    paths.push(...extractLeafPaths(val, path))
  }
  return paths
}

function resolveNestedPath(obj: unknown, dotPath: string): boolean {
  const segments = dotPath.split('.')
  let cur: unknown = obj
  for (const seg of segments) {
    if (cur == null || typeof cur !== 'object') return false
    cur = (cur as Record<string, unknown>)[seg]
  }
  return cur !== undefined && cur !== null && cur !== ''
}

function VariableBrowser({
  variables,
  onInsert,
  rawPayload,
}: {
  variables: TemplateVariable[]
  onInsert: (rawName: string) => void
  rawPayload: string
}) {
  const [open, setOpen] = useState(false)
  const scalars = variables.filter(v => v.variableKindLookupId === VariableKind.SCALAR)
  const loops = variables.filter(v => v.variableKindLookupId === VariableKind.LOOP)
  const conds = variables.filter(v => v.variableKindLookupId === VariableKind.CONDITIONAL)

  const filledKeys = useMemo(() => {
    try {
      const p = JSON.parse(rawPayload)
      return new Set(
        Object.entries(p)
          .filter(([, val]) => val !== '' && val !== null && val !== undefined)
          .map(([k]) => normalize(k))
      )
    } catch { return new Set<string>() }
  }, [rawPayload])

  const missingCount = scalars.filter(v => !v.loopKey && !filledKeys.has(normalize(v.normalizedKey))).length

  return (
    <div style={{
      border: `1px solid ${colors.border}`,
      borderRadius: radius.sm,
      overflow: 'hidden',
    }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%',
          padding: `${spacing.xs} ${spacing.sm}`,
          background: colors.background,
          border: 'none',
          textAlign: 'left',
          fontSize: fontSize.sm,
          fontWeight: 500,
          color: colors.textSecondary,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: spacing.xs,
        }}
      >
        <span style={{ fontSize: '10px' }}>{open ? '▾' : '▸'}</span>
        Insert variable
        {missingCount > 0 && (
          <span style={{
            marginLeft: 'auto',
            fontSize: '11px',
            color: colors.warning,
            fontWeight: 600,
          }}>
            {missingCount} unset
          </span>
        )}
      </button>
      {open && (
        <div style={{ padding: spacing.sm, display: 'flex', flexWrap: 'wrap', gap: '4px', background: colors.surface }}>
          {[...scalars, ...loops, ...conds].map(v => {
            const isLoop = v.variableKindLookupId === VariableKind.LOOP
            const isCond = v.variableKindLookupId === VariableKind.CONDITIONAL
            const filled = isLoop || isCond || filledKeys.has(normalize(v.normalizedKey))
            return (
              <button
                key={v.normalizedKey}
                onClick={() => onInsert(v.rawName)}
                title={v.contextSnippet ?? v.rawName}
                style={{
                  padding: '2px 8px',
                  border: `1px solid ${filled ? colors.border : colors.warning}`,
                  borderRadius: 999,
                  background: filled ? colors.background : colors.warningLight,
                  color: isLoop ? colors.primary : isCond ? colors.warning : colors.text,
                  fontSize: '11px',
                  fontFamily: 'monospace',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                {!isLoop && !isCond && (
                  <span style={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    background: filled ? colors.success : colors.warning,
                    flexShrink: 0,
                    display: 'inline-block',
                  }} />
                )}
                {`{{${v.rawName}}}`}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function ScalarField({ variable: v, value, onChange, hasValue }: {
  variable: TemplateVariable
  value: string
  onChange: (val: string) => void
  hasValue?: boolean
}) {
  const filled = hasValue ?? !!value.trim()
  return (
    <div>
      <label style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: fontSize.sm,
        fontWeight: 500,
        color: colors.text,
        marginBottom: '4px',
        cursor: 'default',
      }}>
        <span style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: filled ? colors.success : colors.warning,
          flexShrink: 0,
          display: 'inline-block',
        }} />
        {v.rawName}
        {v.contextSnippet && (
          <span style={{ color: colors.textMuted, fontWeight: 400 }}>
            — {v.contextSnippet}
          </span>
        )}
      </label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={`{{${v.rawName}}}`}
        style={{
          width: '100%',
          padding: `${spacing.sm} ${spacing.md}`,
          border: `1px solid ${filled ? colors.border : colors.warning}`,
          borderRadius: radius.sm,
          fontSize: fontSize.base,
          color: colors.text,
          background: colors.surface,
          boxSizing: 'border-box',
          outline: 'none',
        }}
        onFocus={e => { e.target.style.borderColor = colors.borderFocus }}
        onBlur={e => { e.target.style.borderColor = filled ? colors.border : colors.warning }}
      />
    </div>
  )
}

function ToggleGroup({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[]
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div style={{
      display: 'flex',
      border: `1px solid ${colors.border}`,
      borderRadius: radius.sm,
      overflow: 'hidden',
    }}>
      {options.map(opt => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          style={{
            padding: `4px 12px`,
            border: 'none',
            background: opt.value === value ? colors.primary : 'transparent',
            color: opt.value === value ? '#fff' : colors.textSecondary,
            fontSize: fontSize.sm,
            fontWeight: opt.value === value ? 600 : 400,
            cursor: 'pointer',
            transition: 'background 0.1s',
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}
